/* eslint-disable max-lines */
import { useAtom, useAtomValue, useSetAtom } from "jotai";
import { useCallback, useLayoutEffect, useMemo, useRef } from "react";

import {
  useArtwork,
  useArtworkForm,
  useArtworkImage,
  useCalculatedArtworkDimensions,
  useEnhancedImageSource
} from "@design-lab/entities/artwork";
import { applyScale, calculateScaleFromSize } from "@design-lab/entities/artwork/lib/artworkScale";
import { useDesignProjectSync } from "@design-lab/features/design-project";
import { useArtworkSelection } from "@design-lab/features/weave-design-lab/model/useArtworkSelection";
import { SparkleIcon } from "@design-lab/features/weave-design-lab/ui";
import { ARTWORK_PROCESSING } from "@design-lab/shared/constants";
import { getSideFromPlacement } from "@design-lab/shared/lib/artworkUtils";
import { Card, CardContent } from "@design-lab/shared/ui/card";
import { Loader } from "@design-lab/shared/ui/loader";
import { useArtboard } from "@design-lab/widgets/artboard/model/useArtboard";

import { Canvas } from "@design-lab/features/canvas";
import { toFileObject } from "@design-lab/shared";
import { Button } from "@design-lab/shared/ui";
import {
  artboardBackgroundAtom,
  RefineArtboard,
  refineArtworkOpenAtom
} from "@design-lab/widgets/artboard";
import { SideSwitcher } from "@design-lab/widgets/artboard/ui/SideSwitcher";
import { Zoom } from "@design-lab/widgets/artboard/ui/Zoom";
import { LoadingBox } from "@design-lab/widgets/artboard/ui/loading-box";
import { BackgroundToggle } from "@design-lab/widgets/artboard/ui/tool-bar/BackgroundToggle";

/**
 * Simplified Artboard for Summary view
 * Shows only essential controls: Background Toggle, Zoom, and Refine Design
 * Hides Size/Placement button
 */
export function SummaryArtboard() {
  const { form } = useArtworkForm();
  const { front, back } = useArtwork();
  const { deleteArtwork } = useArtworkImage();
  const { composition, placement } = useArtboard();
  const side = placement === "back" ? "back" : "front";
  const { source, artworkId } = useEnhancedImageSource({ side });
  const { isRestoring } = useDesignProjectSync();
  const openRefine = useSetAtom(refineArtworkOpenAtom);

  const [backgroundType, setBackgroundType] = useAtom(artboardBackgroundAtom);
  const isRefineOpen = useAtomValue(refineArtworkOpenAtom);
  const { clearSelection } = useArtworkSelection();

  const machine = side === "front" ? front : back;

  const imagePreview = useMemo(() => {
    // If we have a file (normal upload flow), create object URL
    if (machine.state.context.file) {
      return toFileObject(machine.state.context.file, machine.state.context.originalImageUrl);
    }

    // If we have artwork but no file (enhancements flow), use original image URL
    if (machine.state.context.artwork) {
      return { url: machine.state.context.artwork.originalImageUrl };
    }

    return undefined;
  }, [
    machine.state.context.file,
    machine.state.context.artwork,
    machine.state.context.originalImageUrl
  ]);

  const onArtworkRemove = useCallback(() => {
    deleteArtwork(placement, artworkId);
    clearSelection(side);
  }, [deleteArtwork, placement, artworkId, clearSelection, side]);

  const surface = composition?.surfaces[placement];

  const artworkSizeBounds = useCalculatedArtworkDimensions(source.enhancedImage?.image, surface);

  const prevValuesRef = useRef({
    surface,
    artworkImage: source.enhancedImage?.image,
    placement
  });

  useLayoutEffect(() => {
    if (!artworkSizeBounds || artworkSizeBounds.maxWidthInches === 0) {
      return;
    }

    const prev = prevValuesRef.current;
    const artworkImageChanged = prev.artworkImage !== source.enhancedImage?.image;
    const placementChanged = prev.placement !== placement;

    prevValuesRef.current = {
      surface,
      artworkImage: source.enhancedImage?.image,
      placement
    };

    const currentSize = form.getValues(`${placement}.size`);
    const infoPath = getSideFromPlacement(placement);
    const currentScale = form.getValues(`info.${infoPath}.scale`);
    const { minWidthInches, minHeightInches, maxWidthInches, maxHeightInches } = artworkSizeBounds;

    if (!currentSize || (currentSize as any)?.width === 0 || (currentSize as any)?.height === 0) {
      const newSize = {
        width: maxWidthInches,
        height: maxHeightInches
      };

      form.setValue(`${placement}.size`, newSize);
      form.setValue(`info.${infoPath}.scale`, 1.0);
      return;
    }

    if (currentScale && (artworkImageChanged || placementChanged)) {
      const newSize = applyScale(currentScale, artworkSizeBounds);

      if (newSize.width !== currentSize.width || newSize.height !== currentSize.height) {
        form.setValue(`${placement}.size`, newSize);
      }
      return;
    }

    const { width, height } = currentSize as any;
    const currentAspectRatio = Number((width / height).toFixed(2));

    let finalSize: { width: number; height: number };

    if (currentAspectRatio !== artworkSizeBounds.maxAspectRatioByWidth) {
      if (artworkSizeBounds.maxAspectRatioByWidth > 1) {
        // Image is wide: keep current width, calculate height based on aspect ratio
        const newHeight = width / artworkSizeBounds.maxAspectRatioByWidth;
        finalSize = {
          width,
          height: Math.max(minHeightInches, Math.min(newHeight, maxHeightInches))
        };
      } else {
        // Image is tall: keep current height, calculate width based on aspect ratio
        const newWidth = height * artworkSizeBounds.maxAspectRatioByWidth;
        finalSize = {
          width: Math.max(minWidthInches, Math.min(newWidth, maxWidthInches)),
          height
        };
      }
    } else {
      finalSize = {
        width: Math.max(minWidthInches, Math.min(width, maxWidthInches)),
        height: Math.max(minHeightInches, Math.min(height, maxHeightInches))
      };
    }

    if (finalSize.width !== width || finalSize.height !== height) {
      form.setValue(`${placement}.size`, finalSize);
    }

    const scale = calculateScaleFromSize(finalSize, artworkSizeBounds);
    form.setValue(`info.${infoPath}.scale`, scale);
  }, [artworkSizeBounds, form, placement, source.enhancedImage?.image, surface]);

  // Show loading state while restoring design project or if composition is not ready
  if (isRestoring || !composition || !surface) {
    return <Loader message="Creating preview" />;
  }

  const isLoading = machine.state.hasTag("loading");

  const referenceBoundingBox =
    composition.surfaces?.front?.boundingBox || composition.surfaces?.frontLeftChest?.boundingBox;
  const frontSurface = composition.surfaces?.front || composition.surfaces?.frontLeftChest;

  const hasArtworkId = form.watch(`${side}.artworkId`);

  // Render Refine Artwork overlay shell and skip the rest of the Artboard UI
  if (isRefineOpen) {
    return (
      <Card className="h-full flex-1 overflow-hidden rounded-none lg:rounded-xl">
        <CardContent className="relative h-full p-0">
          <RefineArtboard
            frontSurface={frontSurface}
            referenceBoundingBox={referenceBoundingBox}
            surface={surface}
            enhancedImage={source.enhancedImage}
            placement={placement}
          />
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="h-full flex-1 overflow-hidden rounded-none lg:rounded-xl">
      <CardContent className="relative h-full p-0">
        <SideSwitcher />
        <Zoom maxZoom={10} />

        <div className="bg-background absolute bottom-4 left-2 z-10 rounded-md">
          <BackgroundToggle
            value={backgroundType}
            onValueChange={setBackgroundType}
            forceFullWidth
            label="Switch View"
          />
        </div>

        {hasArtworkId && (
          <div className="absolute bottom-4 right-2 z-10">
            <Button
              aria-label="Edit Design"
              variant="outline"
              size="lg"
              data-testid="refine-artwork-open"
              onPress={() => openRefine(true)}
              className="h-10.5 p-0 px-2 text-xs font-semibold"
            >
              <span className="size-6">
                <SparkleIcon className="size-6" />
              </span>
              Edit Design
            </Button>
          </div>
        )}

        <Canvas
          surface={surface}
          referenceBoundingBox={referenceBoundingBox}
          placement={placement}
          readonly={false}
          artworkImage={isLoading ? undefined : source.enhancedImage?.image}
          onArtworkRemove={onArtworkRemove}
          frontSurface={frontSurface}
          backgroundType={backgroundType}
        />

        {isLoading && (
          <div className="absolute left-1/2 top-1/2 z-10 flex w-full -translate-x-1/2 -translate-y-1/2 items-center justify-center p-2">
            <LoadingBox
              imagePreview={imagePreview}
              startTime={machine.state.context.startTime}
              estimatedDuration={ARTWORK_PROCESSING.ESTIMATED_DURATION}
            />
          </div>
        )}
      </CardContent>
    </Card>
  );
}
